// Copyright (C) 2025, Lifefinder Systems International AB, all rights reserved.
import React, { useState } from 'react';

import type { BeaconExpectedState, CheckinStation, CheckinViewProps } from '../../../types';
import { checkinAPI } from '../../../utils/apiClient';
import { formatRelativeTime } from '../../../utils/dateUtils';
import { useWarnings } from '../../../utils/hooks';
import logger from '../../../utils/logger';
import { WARNING_TYPE_LABELS } from '../../../utils/maintenanceUtils';
import { Button } from '../../common/Button';

import { ReplaceDeviceModal } from './ReplaceDeviceModal';

/** Get color for temperature value */
const getTempColor = (temp: number | null | undefined): string => {
  if (temp == null) return '#999';
  if (temp > 50 || temp < -5) return '#f44336';
  if (temp > 35 || temp < 10) return '#ff9800';
  return '#4caf50';
};

/** Get color for humidity value */
const getHumidityColor = (hum: number | null | undefined): string => {
  if (hum == null) return '#999';
  if (hum > 85) return '#f44336';
  if (hum > 70 || hum < 20) return '#ff9800';
  return '#4caf50';
};

/**
 * Returns true when a specific config field has been sent to the device
 * but not yet confirmed via port-32 echo.
 */
const isConfigPending = (
  checkin: CheckinStation,
  field: 'mac' | 'channel' | 'power' | 'datarate'
): boolean => {
  if (!checkin.pending_config || !(field in checkin.pending_config)) return false;
  if (!checkin.pending_config_at) return false;
  if (!checkin.confirmed_config_at) return true;
  return new Date(checkin.pending_config_at) > new Date(checkin.confirmed_config_at);
};

/** Inline badge shown next to a config value while waiting for device confirmation. */
const PendingBadge: React.FC = () => (
  <span
    style={{
      marginLeft: '0.5vmin',
      fontSize: '1.1vmin',
      background: '#fff3cd',
      color: '#856404',
      border: '1px solid #ffc107',
      borderRadius: '0.3vmin',
      padding: '0 0.4vmin',
      fontWeight: 600,
    }}
    title="Sent to device — waiting for confirmation"
  >
    Pending
  </span>
);

/** Get color for signal strength value */
const getSignalColor = (rssi: number | null | undefined): string => {
  if (rssi == null) return '#999';
  if (rssi < -100) return '#f44336';
  if (rssi < -85) return '#ff9800';
  return '#4caf50';
};

/** Format number to 1 decimal */
const fmt1 = (v: number | null | undefined): string => (v != null ? v.toFixed(1) : '-');

interface CheckinStatus {
  status: string;
  icon: string;
  label: string;
  bgColor: string;
  borderColor: string;
}

/**
 * Returns true when a downlink has been confirmed by the device but the confirmed
 * state differs from expected — i.e. waiting for the device to apply the command.
 * confirmed_state = null/undefined means no uplink received yet → not pending.
 */
const isCheckinPending = (checkin: CheckinStation): boolean => {
  if (!checkin.confirmed_state) return false;
  return checkin.expected_state !== checkin.confirmed_state;
};

/**
 * Determine check-in station status and styling based on expected_state and responsiveness.
 *
 * Priority: expected_state drives display, last_seen (1h threshold) indicates health.
 * - OFF / Pending off: expected_state is 'off'
 * - NOT RESPONDING: expected ON but not seen within 1 hour
 * - ON (warning) / Pending on: responding but an active signal_weak warning is present
 * - ON (ok) / Pending on: responding with good signal
 *
 * Weak signal is driven by the backend warning system (DR-based, see
 * background-maintenance.js), not by RSSI — RSSI is not a reliable proxy for
 * sustained link quality since ADR is always active.
 *
 * Label shows "Pending on/off" when expected_state !== confirmed_state (and
 * confirmed_state is not null). Color stays based on confirmed/actual state.
 *
 * @param {CheckinStation} checkin - CheckinStation object with expected_state, confirmed_state, last_seen
 * @param {boolean} hasWeakSignalWarning - Whether an active signal_weak warning exists for this device
 * @returns {CheckinStatus} Status object with icon, label, colors for rendering
 */
const getCheckinStatus = (
  checkin: CheckinStation,
  hasWeakSignalWarning: boolean
): CheckinStatus => {
  const now = Date.now();
  const lastSeen = checkin.last_seen ? new Date(checkin.last_seen).getTime() : 0;
  const ageHours = (now - lastSeen) / (1000 * 60 * 60);

  const isResponding = ageHours < 1;
  const expectedOn = checkin.expected_state === 'on' || !checkin.expected_state;
  const pending = isCheckinPending(checkin);

  // Pending state — color reflects confirmed (actual) state, label signals what's in progress
  if (pending) {
    if (checkin.confirmed_state === 'on') {
      // Device is ON, command to turn OFF is in progress
      return {
        status: 'ok',
        icon: '🟢',
        label: 'Pending OFF',
        bgColor: 'white',
        borderColor: '#4CAF50',
      };
    } else {
      // Device is OFF, command to turn ON is in progress
      return {
        status: 'off',
        icon: '⚪',
        label: 'Pending ON',
        bgColor: '#f5f5f5',
        borderColor: '#999',
      };
    }
  }

  if (!expectedOn) {
    return {
      status: 'off',
      icon: '⚪',
      label: 'OFF',
      bgColor: '#f5f5f5',
      borderColor: '#999',
    };
  }

  if (!isResponding) {
    return {
      status: 'critical',
      icon: '🔴',
      label: 'NOT RESPONDING',
      bgColor: '#ffebee',
      borderColor: '#f44',
    };
  }

  if (hasWeakSignalWarning) {
    return {
      status: 'warning',
      icon: '🟡',
      label: 'ON',
      bgColor: '#fff3e0',
      borderColor: '#ff9800',
    };
  }

  return {
    status: 'ok',
    icon: '🟢',
    label: 'ON',
    bgColor: 'white',
    borderColor: '#4CAF50',
  };
};

/** Geofence mode options (0–5), shared with BeaconView. */
const GEOFENCE_MODES = [
  { value: 0, label: '0 — Disabled' },
  { value: 1, label: '1 — Always overwrite' },
  { value: 2, label: '2 — Fallback only (solver > 200 m)' },
  { value: 3, label: '3 — Hybrid: geofence lat/lng + solver floor' },
  { value: 4, label: '4 — Hybrid: solver lat/lng + geofence floor' },
  { value: 5, label: '5 — Floor interpolation' },
] as const;

interface CheckinUpdates {
  deck?: number;
  side?: string;
  frame_number?: number | null;
  channel?: number | null;
  mac_address?: string | null;
  expected_state?: 'on' | 'off' | 'auto';
  output_power?: number | null;
  datarate?: number | null;
  lat?: number | null;
  lng?: number | null;
  floor?: number | null;
  accuracy?: number | null;
  geofence?: number;
  geofence_threshold?: number | null;
  geofence_area?: string | null;
}

/**
 * CheckinView Component - LF Check-in Station management
 *
 * Displays LF Check-in Stations with:
 * - Status indicators (active/inactive)
 * - Last check-in time
 * - Check-in count
 * - Warning indicators
 * - Edit functionality for location and zone
 *
 * @example
 * <CheckinView
 *   checkins={checkins}
 *   onToggleCheckin={handleToggleCheckin}
 *   onRefresh={fetchCheckins}
 * />
 */
export const CheckinView: React.FC<CheckinViewProps> = ({
  checkins,
  onToggleCheckin,
  onRefresh,
  isSuperAdmin,
}) => {
  const [selectedCheckin, setSelectedCheckin] = useState<CheckinStation | null>(null);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [replaceMode, setReplaceMode] = useState<boolean>(false);
  const [editedDeck, setEditedDeck] = useState<string>('');
  const [editedSide, setEditedSide] = useState<string>('');
  const [editedFrameNumber, setEditedFrameNumber] = useState<string>('');
  const [editedChannel, setEditedChannel] = useState<string>('');
  const [editedMacAddress, setEditedMacAddress] = useState<string>('');
  const [editedExpectedState, setEditedExpectedState] = useState<BeaconExpectedState>('on');
  const [editedOutputPower, setEditedOutputPower] = useState<string>('');
  const [editedDatarate, setEditedDatarate] = useState<string>('');
  const [editedGeofence, setEditedGeofence] = useState<number>(0);
  const [editedGeofenceThreshold, setEditedGeofenceThreshold] = useState<string>('');
  const [editedGeofenceArea, setEditedGeofenceArea] = useState<string>('');
  const [editedLat, setEditedLat] = useState<string>('');
  const [editedLng, setEditedLng] = useState<string>('');
  const [editedFloor, setEditedFloor] = useState<string>('');
  const [editedAccuracy, setEditedAccuracy] = useState<string>('');

  // Use shared warnings hook
  const { warnings: activeWarnings, getWarningsForDevice } = useWarnings({
    category: 'lf_checkin',
  });

  /**
   * Handle checkin update
   */
  const handleUpdateCheckin = async (checkinId: string, updates: CheckinUpdates): Promise<void> => {
    try {
      const data = await checkinAPI.update(checkinId, updates);
      if (data.success) {
        logger.debug(`Check-in station ${checkinId} updated`);
        setEditMode(false);
        setSelectedCheckin(null);
        if (onRefresh) onRefresh();
      } else {
        alert(`Failed to update check-in station: ${data.error}`);
      }
    } catch (err) {
      console.error('Error updating check-in station:', err);
      alert(
        `Failed to update check-in station: ${err instanceof Error ? err.message : 'Unknown error'}`
      );
    }
  };

  /**
   * Open edit mode
   */
  const openEditMode = (checkin: CheckinStation): void => {
    setEditMode(true);
    setEditedDeck(checkin.deck != null ? checkin.deck.toString() : '0');
    setEditedSide(checkin.side || 'Unknown');
    setEditedFrameNumber(checkin.frame_number != null ? checkin.frame_number.toString() : '');
    setEditedChannel(
      checkin.channel !== null && checkin.channel !== undefined ? checkin.channel.toString() : ''
    );
    setEditedMacAddress(checkin.mac_address || '');
    setEditedExpectedState(checkin.expected_state || 'on');
    setEditedOutputPower(
      checkin.output_power !== null && checkin.output_power !== undefined
        ? checkin.output_power.toString()
        : ''
    );
    setEditedDatarate(
      checkin.datarate !== null && checkin.datarate !== undefined ? checkin.datarate.toString() : ''
    );
    setEditedLat(checkin.lat != null ? checkin.lat.toString() : '');
    setEditedLng(checkin.lng != null ? checkin.lng.toString() : '');
    setEditedFloor(checkin.floor != null ? checkin.floor.toString() : '');
    setEditedAccuracy(checkin.accuracy != null ? checkin.accuracy.toString() : '');
    setEditedGeofence(checkin.geofence ?? 0);
    setEditedGeofenceThreshold(
      checkin.geofence_threshold != null ? checkin.geofence_threshold.toString() : ''
    );
    setEditedGeofenceArea(checkin.geofence_area || '');
  };

  /**
   * Save checkin edits
   */
  const saveCheckinEdit = (): void => {
    if (!selectedCheckin) return;

    const updates: CheckinUpdates = {};
    const currentDeck = selectedCheckin.deck != null ? selectedCheckin.deck.toString() : '0';
    if (editedDeck !== currentDeck) {
      updates.deck = parseInt(editedDeck);
    }
    if (editedSide !== selectedCheckin.side) {
      updates.side = editedSide;
    }
    const currentFrameNumber =
      selectedCheckin.frame_number != null ? selectedCheckin.frame_number.toString() : '';
    if (editedFrameNumber !== currentFrameNumber) {
      updates.frame_number = editedFrameNumber ? parseInt(editedFrameNumber) : null;
    }
    const currentChannel =
      selectedCheckin.channel !== null && selectedCheckin.channel !== undefined
        ? selectedCheckin.channel.toString()
        : '';
    if (editedChannel !== currentChannel) {
      updates.channel = editedChannel ? parseInt(editedChannel) : null;
    }
    if (editedMacAddress !== (selectedCheckin.mac_address || '')) {
      updates.mac_address = editedMacAddress || null;
    }
    if (editedExpectedState !== (selectedCheckin.expected_state || 'on')) {
      updates.expected_state = editedExpectedState;
    }
    const currentOutputPower =
      selectedCheckin.output_power !== null && selectedCheckin.output_power !== undefined
        ? selectedCheckin.output_power.toString()
        : '';
    if (editedOutputPower !== currentOutputPower) {
      updates.output_power = editedOutputPower ? parseInt(editedOutputPower) : null;
    }
    const currentDatarate =
      selectedCheckin.datarate !== null && selectedCheckin.datarate !== undefined
        ? selectedCheckin.datarate.toString()
        : '';
    if (editedDatarate !== currentDatarate) {
      updates.datarate = editedDatarate ? parseInt(editedDatarate) : null;
    }
    const currentLat = selectedCheckin.lat != null ? selectedCheckin.lat.toString() : '';
    if (editedLat !== currentLat) {
      updates.lat = editedLat ? parseFloat(editedLat) : null;
    }
    const currentLng = selectedCheckin.lng != null ? selectedCheckin.lng.toString() : '';
    if (editedLng !== currentLng) {
      updates.lng = editedLng ? parseFloat(editedLng) : null;
    }
    const currentFloor = selectedCheckin.floor != null ? selectedCheckin.floor.toString() : '';
    if (editedFloor !== currentFloor) {
      updates.floor = editedFloor ? parseInt(editedFloor) : null;
    }
    const currentAccuracy =
      selectedCheckin.accuracy != null ? selectedCheckin.accuracy.toString() : '';
    if (editedAccuracy !== currentAccuracy) {
      updates.accuracy = editedAccuracy ? parseFloat(editedAccuracy) : null;
    }
    const currentGeofenceThreshold =
      selectedCheckin.geofence_threshold != null
        ? selectedCheckin.geofence_threshold.toString()
        : '';
    if (editedGeofence !== (selectedCheckin.geofence ?? 0)) {
      updates.geofence = editedGeofence;
    }
    if (editedGeofenceThreshold !== currentGeofenceThreshold) {
      updates.geofence_threshold = editedGeofenceThreshold
        ? parseInt(editedGeofenceThreshold)
        : null;
    }
    if (editedGeofenceArea !== (selectedCheckin.geofence_area || '')) {
      updates.geofence_area = editedGeofenceArea || null;
    }

    if (Object.keys(updates).length > 0) {
      handleUpdateCheckin(selectedCheckin.id, updates);
    }
    setEditMode(false);
    setSelectedCheckin(null);
  };

  /**
   * Handle device replacement completion
   */
  const handleDeviceReplacement = (): void => {
    setReplaceMode(false);
    setSelectedCheckin(null);
    // Refresh data to show updated devices
    if (onRefresh) onRefresh();
  };

  return (
    <div>
      {/* Overall statistics */}
      <div
        style={{
          background: '#f9f9f9',
          padding: '1.5vmin',
          borderRadius: '0.5vmin',
          marginBottom: '2vmin',
          display: 'flex',
          gap: '2vmin',
          fontSize: '1.8vmin',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <strong>📊 Total:</strong> {checkins.length} check-in stations
        </div>
        <div>
          <strong>🟢 ON:</strong>{' '}
          {checkins.filter((c) => c.expected_state === 'on' || !c.expected_state).length}
        </div>
        <div>
          <strong>⚪ OFF:</strong> {checkins.filter((c) => c.expected_state === 'off').length}
        </div>
      </div>

      {/* Active warnings banner */}
      {activeWarnings.length > 0 && (
        <div
          style={{
            marginBottom: '2vmin',
            borderRadius: '0.5vmin',
            border: `2px solid ${activeWarnings.some((w) => w.severity === 'critical') ? '#f44336' : '#ff9800'}`,
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              background: activeWarnings.some((w) => w.severity === 'critical')
                ? '#f44336'
                : '#ff9800',
              color: 'white',
              padding: '0.8vmin 1.5vmin',
              fontSize: '1.6vmin',
              fontWeight: 'bold',
            }}
          >
            {activeWarnings.some((w) => w.severity === 'critical') ? '🔴' : '⚠️'} Active Warnings (
            {activeWarnings.length})
          </div>
          <div style={{ background: '#fffdf9', padding: '1vmin' }}>
            {activeWarnings.map((warning) => {
              const checkin = checkins.find((c) => c.device_id === warning.device);
              const isCritical = warning.severity === 'critical';
              return (
                <div
                  key={warning.id}
                  onClick={() => checkin && setSelectedCheckin(checkin)}
                  style={{
                    fontSize: '1.5vmin',
                    padding: '0.8vmin 1vmin',
                    marginBottom: '0.5vmin',
                    background: isCritical ? '#ffebee' : '#fff8e1',
                    borderLeft: `4px solid ${isCritical ? '#f44336' : '#ff9800'}`,
                    borderRadius: '0.3vmin',
                    cursor: checkin ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '1vmin',
                  }}
                >
                  <span
                    style={{
                      display: 'inline-block',
                      background: isCritical ? '#f44336' : '#ff9800',
                      color: 'white',
                      fontSize: '1.1vmin',
                      fontWeight: 'bold',
                      padding: '0.2vmin 0.6vmin',
                      borderRadius: '1vmin',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isCritical ? '🔴 Critical' : '⚠ Warning'}
                  </span>
                  <span style={{ flex: 1 }}>
                    <strong>{checkin?.name ?? warning.device}</strong>
                    {' — '}
                    {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                  </span>
                  <span style={{ fontSize: '1.3vmin', color: '#888', whiteSpace: 'nowrap' }}>
                    {new Date(warning.detected_at).toLocaleString()}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Check-in stations list */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
          gap: '2vmin',
        }}
      >
        {checkins.length === 0 ? (
          <div
            style={{
              gridColumn: '1 / -1',
              textAlign: 'center',
              padding: '4vmin',
              fontSize: '2vmin',
              color: '#666',
            }}
          >
            No check-in stations found
          </div>
        ) : (
          checkins.map((checkin) => {
            const hasWeakSignalWarning = getWarningsForDevice(checkin.device_id).some(
              (w) => w.warning_type === 'signal_weak'
            );
            const status = getCheckinStatus(checkin, hasWeakSignalWarning);
            return (
              <div
                key={checkin.id}
                onClick={() => setSelectedCheckin(checkin)}
                style={{
                  background: status.bgColor,
                  borderRadius: '1vmin',
                  padding: '1.5vmin',
                  border: `2px solid ${status.borderColor}`,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'transform 0.1s',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.transform = 'translateY(-2px)')}
                onMouseLeave={(e) => (e.currentTarget.style.transform = 'translateY(0)')}
              >
                <div
                  style={{
                    fontSize: '2vmin',
                    fontWeight: 'bold',
                    marginBottom: '1vmin',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>{checkin.name}</span>
                  <span style={{ fontSize: '1.8vmin' }}>
                    {status.icon} {status.label}
                  </span>
                </div>

                <div style={{ fontSize: '1.6vmin', color: '#666', marginBottom: '0.5vmin' }}>
                  <strong>Deck:</strong> {checkin.deck !== null ? checkin.deck : 'N/A'}
                </div>

                <div style={{ fontSize: '1.6vmin', color: '#666', marginBottom: '0.5vmin' }}>
                  <strong>Side:</strong> {checkin.side || 'N/A'}
                </div>

                <div style={{ fontSize: '1.4vmin', color: '#888', marginTop: '1vmin' }}>
                  <div>Frame No: {checkin.frame_number || 'N/A'}</div>
                  <div>Device ID: {checkin.device_id}</div>
                  <div>Last seen: {formatRelativeTime(checkin.last_seen)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Checkin detail popup */}
      {selectedCheckin && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            // flex-start + paddingTop instead of center: prevents modal from extending below the visible
            // viewport on iOS where 100vh includes browser chrome behind the address/tab bar
            alignItems: 'flex-start',
            paddingTop: 'max(20px, 5vh)',
            zIndex: 3000,
          }}
          onClick={() => {
            setEditMode(false);
            setSelectedCheckin(null);
          }}
        >
          <div
            style={{
              background: 'white',
              borderRadius: '1vmin',
              padding: '2vmin',
              maxWidth: '500px',
              width: '90%',
              // dvh (dynamic viewport height) excludes browser chrome — ensures modal fits visible area on iOS
              maxHeight: 'min(90vh, 85dvh)',
              overflowY: 'scroll',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              boxShadow: '0 4px 8px rgba(0,0,0,0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '2vmin',
                borderBottom: '2px solid #ddd',
                paddingBottom: '1vmin',
              }}
            >
              <h2 style={{ margin: 0, fontSize: '2.5vmin' }}>
                {editMode ? 'Edit Check-in Station' : selectedCheckin.name}
              </h2>
              <Button
                text="✕"
                onClick={() => {
                  setEditMode(false);
                  setSelectedCheckin(null);
                }}
                style={{
                  background: '#f44',
                  color: 'white',
                  fontSize: '2vmin',
                  padding: '0.5vmin 1vmin',
                }}
              />
            </div>

            {/* Details or Edit Form */}
            {editMode ? (
              <div style={{ fontSize: '1.8vmin' }}>
                {/* Reusable field style */}
                {(() => {
                  const inputStyle: React.CSSProperties = {
                    width: '100%',
                    padding: '0.8vmin',
                    fontSize: '1.6vmin',
                    border: '1px solid #ccc',
                    borderRadius: '0.4vmin',
                    boxSizing: 'border-box',
                  };
                  const labelStyle: React.CSSProperties = {
                    display: 'block',
                    marginBottom: '0.3vmin',
                    fontWeight: 'bold',
                    fontSize: '1.5vmin',
                  };
                  const fieldStyle: React.CSSProperties = { marginBottom: '1vmin' };
                  const grid2: React.CSSProperties = {
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    gap: '1vmin',
                  };
                  const sectionHeader: React.CSSProperties = {
                    borderTop: '1px solid #ddd',
                    marginTop: '1vmin',
                    paddingTop: '1vmin',
                    marginBottom: '1vmin',
                    fontWeight: 'bold',
                    fontSize: '1.5vmin',
                    color: '#555',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  };
                  return (
                    <>
                      <div style={grid2}>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Deck</label>
                          <input
                            type="number"
                            value={editedDeck}
                            onChange={(e) => setEditedDeck(e.target.value)}
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Side</label>
                          <input
                            type="text"
                            value={editedSide}
                            onChange={(e) => setEditedSide(e.target.value)}
                            placeholder="e.g. Port, Starboard"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Frame No.</label>
                          <input
                            type="number"
                            value={editedFrameNumber}
                            onChange={(e) => setEditedFrameNumber(e.target.value)}
                            placeholder="Optional"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Channel</label>
                          <input
                            type="number"
                            value={editedChannel}
                            onChange={(e) => setEditedChannel(e.target.value)}
                            placeholder="Optional"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                      <div style={fieldStyle}>
                        <label style={labelStyle}>MAC Address</label>
                        <input
                          type="text"
                          value={editedMacAddress}
                          onChange={(e) => setEditedMacAddress(e.target.value)}
                          placeholder="Optional"
                          style={inputStyle}
                        />
                      </div>
                      <div style={grid2}>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Expected State:</label>
                          <select
                            value={editedExpectedState}
                            onChange={(e) =>
                              setEditedExpectedState(e.target.value as BeaconExpectedState)
                            }
                            style={inputStyle}
                          >
                            <option value="on">On</option>
                            <option value="off">Off</option>
                            <option value="auto">Auto</option>
                          </select>
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Output Power (dB)</label>
                          <input
                            type="number"
                            value={editedOutputPower}
                            onChange={(e) => setEditedOutputPower(e.target.value)}
                            placeholder="2-20"
                            min="2"
                            max="20"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Datarate</label>
                          <input
                            type="number"
                            value={editedDatarate}
                            onChange={(e) => setEditedDatarate(e.target.value)}
                            placeholder="-7 to 54"
                            min="-7"
                            max="54"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Geofence Mode</label>
                          <select
                            value={editedGeofence}
                            onChange={(e) => setEditedGeofence(parseInt(e.target.value))}
                            style={inputStyle}
                          >
                            {GEOFENCE_MODES.map((m) => (
                              <option key={m.value} value={m.value}>
                                {m.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Geofence Threshold (dBm)</label>
                          <input
                            type="number"
                            value={editedGeofenceThreshold}
                            onChange={(e) => setEditedGeofenceThreshold(e.target.value)}
                            placeholder="Tomt = ingen filtrering"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Geofence Area</label>
                          <input
                            type="text"
                            value={editedGeofenceArea}
                            onChange={(e) => setEditedGeofenceArea(e.target.value)}
                            placeholder="t.ex. Reception, Foajé"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                      <div style={sectionHeader}>📍 Locked Position</div>
                      <div style={grid2}>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Latitude</label>
                          <input
                            type="number"
                            value={editedLat}
                            onChange={(e) => setEditedLat(e.target.value)}
                            placeholder="57.6789"
                            step="any"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Longitude</label>
                          <input
                            type="number"
                            value={editedLng}
                            onChange={(e) => setEditedLng(e.target.value)}
                            placeholder="11.9876"
                            step="any"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Floor</label>
                          <input
                            type="number"
                            value={editedFloor}
                            onChange={(e) => setEditedFloor(e.target.value)}
                            placeholder="Optional"
                            style={inputStyle}
                          />
                        </div>
                        <div style={fieldStyle}>
                          <label style={labelStyle}>Accuracy (m)</label>
                          <input
                            type="number"
                            value={editedAccuracy}
                            onChange={(e) => setEditedAccuracy(e.target.value)}
                            placeholder="Optional"
                            min="0"
                            step="any"
                            style={inputStyle}
                          />
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ) : (
              <>
                <div style={{ fontSize: '1.8vmin', lineHeight: '2.5' }}>
                  <div>
                    <strong>Status:</strong>{' '}
                    {(() => {
                      const hasWeakSignalWarning = getWarningsForDevice(
                        selectedCheckin.device_id
                      ).some((w) => w.warning_type === 'signal_weak');
                      const s = getCheckinStatus(selectedCheckin, hasWeakSignalWarning);
                      return `${s.icon} ${s.label}`;
                    })()}
                  </div>
                  <div
                    style={{
                      background: '#f8f9fa',
                      borderRadius: '0.5vmin',
                      padding: '1vmin 1.5vmin',
                      margin: '0.5vmin 0',
                      textAlign: 'center',
                    }}
                  >
                    <div
                      style={{
                        fontSize: '1.4vmin',
                        color: '#888',
                        marginBottom: '0.3vmin',
                        fontWeight: 600,
                      }}
                    >
                      Sensor
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        gap: '2vmin',
                        fontSize: '1.8vmin',
                      }}
                    >
                      <span style={{ color: getTempColor(selectedCheckin.temperature) }}>
                        🌡️ {fmt1(selectedCheckin.temperature)}°C
                      </span>
                      <span style={{ color: getHumidityColor(selectedCheckin.humidity) }}>
                        💧 {fmt1(selectedCheckin.humidity)}%
                      </span>
                    </div>
                    <div style={{ fontSize: '1.8vmin', marginTop: '0.3vmin' }}>
                      <span style={{ color: getSignalColor(selectedCheckin.signal_strength) }}>
                        📶{' '}
                        {selectedCheckin.signal_strength
                          ? `${selectedCheckin.signal_strength} dBm`
                          : '-'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <strong>Deck:</strong>{' '}
                    {selectedCheckin.deck !== null ? selectedCheckin.deck : 'N/A'}
                  </div>
                  <div>
                    <strong>Side:</strong> {selectedCheckin.side || 'N/A'}
                  </div>
                  <div>
                    <strong>Frame No:</strong> {selectedCheckin.frame_number || 'N/A'}
                  </div>
                  {isSuperAdmin && (
                    <>
                      <div>
                        <strong>Channel:</strong> {selectedCheckin.channel || 'N/A'}
                        {isConfigPending(selectedCheckin, 'channel') && <PendingBadge />}
                      </div>
                      <div>
                        <strong>MAC Address:</strong> {selectedCheckin.mac_address || 'N/A'}
                        {isConfigPending(selectedCheckin, 'mac') && <PendingBadge />}
                      </div>
                      <div>
                        <strong>Output Power:</strong>{' '}
                        {selectedCheckin.output_power !== null &&
                        selectedCheckin.output_power !== undefined
                          ? `${selectedCheckin.output_power} dB`
                          : 'N/A'}
                        {isConfigPending(selectedCheckin, 'power') && <PendingBadge />}
                      </div>
                      <div>
                        <strong>Datarate:</strong>{' '}
                        {selectedCheckin.datarate !== null && selectedCheckin.datarate !== undefined
                          ? selectedCheckin.datarate
                          : 'N/A'}
                        {isConfigPending(selectedCheckin, 'datarate') && <PendingBadge />}
                      </div>
                      <div>
                        <strong>Geofence Mode:</strong>{' '}
                        {GEOFENCE_MODES.find((m) => m.value === (selectedCheckin.geofence ?? 0))
                          ?.label ?? 'N/A'}
                      </div>
                      <div>
                        <strong>Geofence Threshold:</strong>{' '}
                        {selectedCheckin.geofence_threshold != null
                          ? `${selectedCheckin.geofence_threshold} dBm`
                          : 'N/A'}
                      </div>
                      {selectedCheckin.geofence_area && (
                        <div>
                          <strong>Geofence Area:</strong> {selectedCheckin.geofence_area}
                        </div>
                      )}
                      <div
                        style={{
                          borderTop: '1px solid #eee',
                          marginTop: '1vmin',
                          paddingTop: '1vmin',
                        }}
                      >
                        <strong>📍 Locked Position:</strong>{' '}
                        {selectedCheckin.lat != null && selectedCheckin.lng != null
                          ? `${selectedCheckin.lat.toFixed(5)}, ${selectedCheckin.lng.toFixed(5)}${selectedCheckin.floor != null ? ` (floor ${selectedCheckin.floor})` : ''}${selectedCheckin.accuracy != null ? ` ±${selectedCheckin.accuracy}m` : ''}`
                          : 'Not set'}
                      </div>
                    </>
                  )}
                  <div>
                    <strong>Device ID:</strong> {selectedCheckin.device_id}
                  </div>
                  <div>
                    <strong>Last seen:</strong> {formatRelativeTime(selectedCheckin.last_seen)}
                  </div>
                  {selectedCheckin.last_state_change && (
                    <div
                      style={{
                        borderTop: '1px solid #eee',
                        marginTop: '1vmin',
                        paddingTop: '1vmin',
                        fontSize: '1.5vmin',
                        color: '#666',
                      }}
                    >
                      <div>
                        <strong>State changed:</strong>{' '}
                        {new Date(selectedCheckin.last_state_change).toLocaleString()}
                      </div>
                      {selectedCheckin.state_changed_by && (
                        <div>
                          <strong>Changed by:</strong> {selectedCheckin.state_changed_by}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Warnings from warning system */}
                {(() => {
                  const deviceWarnings = getWarningsForDevice(selectedCheckin.device_id);
                  if (deviceWarnings.length > 0) {
                    return (
                      <div
                        style={{
                          marginTop: '2vmin',
                          padding: '1.5vmin',
                          background: '#fff3e0',
                          borderRadius: '0.5vmin',
                          border: '2px solid #ff9800',
                        }}
                      >
                        <div
                          style={{ fontSize: '1.8vmin', fontWeight: 'bold', marginBottom: '1vmin' }}
                        >
                          ⚠️ Active Warnings ({deviceWarnings.length})
                        </div>
                        {deviceWarnings.map((warning, idx) => {
                          return (
                            <div
                              key={idx}
                              style={{
                                fontSize: '1.5vmin',
                                padding: '0.8vmin',
                                marginBottom: '0.5vmin',
                                background: warning.severity === 'critical' ? '#ffebee' : '#fff',
                                borderLeft: `3px solid ${warning.severity === 'critical' ? '#f44' : '#ff9800'}`,
                                borderRadius: '0.3vmin',
                              }}
                            >
                              <div style={{ fontWeight: 'bold' }}>
                                {WARNING_TYPE_LABELS[warning.warning_type] || warning.warning_type}
                              </div>
                              <div style={{ fontSize: '1.3vmin', color: '#666' }}>
                                Detected: {new Date(warning.detected_at).toLocaleString()}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  }
                  return null;
                })()}
              </>
            )}

            {/* Control buttons */}
            <div
              style={{
                marginTop: '2vmin',
                paddingTop: '2vmin',
                borderTop: '1px solid #ddd',
                display: 'flex',
                gap: '1vmin',
                flexWrap: 'wrap',
              }}
            >
              {editMode ? (
                <>
                  <Button
                    text="✓ Save"
                    onClick={saveCheckinEdit}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: '#4CAF50',
                      color: 'white',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                  <Button
                    text="Cancel"
                    onClick={() => setEditMode(false)}
                    style={{
                      flex: 1,
                      minWidth: '120px',
                      background: '#ccc',
                      color: '#333',
                      fontSize: '1.6vmin',
                      padding: '1vmin 1.5vmin',
                      border: 'none',
                      borderRadius: '0.5vmin',
                      boxSizing: 'border-box',
                    }}
                  />
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', gap: '0.5vmin', width: '100%' }}>
                    {isSuperAdmin && (
                      <Button
                        text="✏️ Edit"
                        onClick={() => openEditMode(selectedCheckin)}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: '#2196F3',
                          color: 'white',
                          fontSize: '1.6vmin',
                          padding: '1vmin 1.5vmin',
                          border: 'none',
                          borderRadius: '0.5vmin',
                          boxSizing: 'border-box',
                        }}
                      />
                    )}
                    <Button
                      text="🔄 Replace"
                      onClick={() => setReplaceMode(true)}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        background: '#FF9800',
                        color: 'white',
                        fontSize: '1.6vmin',
                        padding: '1vmin 1.5vmin',
                        border: 'none',
                        borderRadius: '0.5vmin',
                        boxSizing: 'border-box',
                      }}
                    />
                  </div>
                  {isSuperAdmin && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '0.5vmin',
                        width: '100%',
                        marginTop: '0.5vmin',
                      }}
                    >
                      <Button
                        text="Turn off"
                        onClick={() => {
                          onToggleCheckin(selectedCheckin.id as unknown as number, 'off');
                          setSelectedCheckin(null);
                        }}
                        disabled={selectedCheckin.expected_state === 'off'}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: selectedCheckin.expected_state === 'off' ? '#ccc' : '#ff9800',
                          color: 'white',
                          fontSize: '1.6vmin',
                          padding: '1vmin 1.5vmin',
                          border: 'none',
                          borderRadius: '0.5vmin',
                          opacity: selectedCheckin.expected_state === 'off' ? 0.5 : 1,
                          cursor:
                            selectedCheckin.expected_state === 'off' ? 'not-allowed' : 'pointer',
                        }}
                      />
                      <Button
                        text="Turn on"
                        onClick={() => {
                          onToggleCheckin(selectedCheckin.id as unknown as number, 'on');
                          setSelectedCheckin(null);
                        }}
                        disabled={selectedCheckin.expected_state === 'on'}
                        style={{
                          flex: 1,
                          minWidth: 0,
                          background: selectedCheckin.expected_state === 'on' ? '#ccc' : '#4CAF50',
                          color: 'white',
                          fontSize: '1.6vmin',
                          padding: '1vmin 1.5vmin',
                          border: 'none',
                          borderRadius: '0.5vmin',
                          opacity: selectedCheckin.expected_state === 'on' ? 0.5 : 1,
                          cursor:
                            selectedCheckin.expected_state === 'on' ? 'not-allowed' : 'pointer',
                        }}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Replace device modal */}
      {replaceMode && selectedCheckin && (
        <ReplaceDeviceModal
          device={selectedCheckin}
          deviceCategory="lf_checkin"
          onReplace={handleDeviceReplacement}
          onCancel={() => setReplaceMode(false)}
        />
      )}
    </div>
  );
};

export default CheckinView;
